import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { setStoredPref } from "../../lib/langPref";
import { useLang, pathForLang } from "../../i18n";

const AUTO_KEY = "sp_auto_redir";

/**
 * Read the auto-redirect flag synchronously on first render. We only delete
 * the localStorage key AFTER we've committed the toast to state, so React
 * StrictMode's double-mount in dev can't drop the value between mounts.
 */
const readFlag = () => {
  try {
    return window.localStorage.getItem(AUTO_KEY);
  } catch {
    return null;
  }
};
const clearFlag = () => {
  try {
    window.localStorage.removeItem(AUTO_KEY);
  } catch {
    /* ignore */
  }
};

const COPY = {
  en: {
    msg: "Showing in English based on your browser.",
    cta: "Cambiar a Español",
    dismissAria: "Dismiss",
  },
  es: {
    msg: "Mostrando en español según tu navegador.",
    cta: "Switch to English",
    dismissAria: "Cerrar",
  },
};

export const LangToast = () => {
  const lang = useLang();
  const loc = useLocation();
  const navigate = useNavigate();

  // Lazy initializer — runs once per React mount (twice in StrictMode dev,
  // but both reads see the same localStorage value because we don't delete
  // it until the toast is dismissed/clicked).
  const [originLang, setOriginLang] = useState(() => {
    const v = readFlag();
    return v && v !== lang && (v === "en" || v === "es") ? v : null;
  });

  // Auto-dismiss after 12s.
  useEffect(() => {
    if (!originLang) return;
    const t = setTimeout(() => {
      clearFlag();
      setOriginLang(null);
    }, 12000);
    return () => clearTimeout(t);
  }, [originLang]);

  if (!originLang) return null;

  const copy = COPY[lang] || COPY.en;
  const targetPath = pathForLang(originLang, loc.pathname) + loc.hash;

  const onSwitch = () => {
    setStoredPref(originLang);
    clearFlag();
    setOriginLang(null);
    navigate(targetPath);
  };

  const onDismiss = () => {
    setStoredPref(lang);
    clearFlag();
    setOriginLang(null);
  };

  return (
    <div
      className="sp-lang-toast"
      role="status"
      aria-live="polite"
      data-testid="lang-toast"
    >
      <span className="sp-lang-toast-msg">{copy.msg}</span>
      <button
        type="button"
        onClick={onSwitch}
        className="sp-lang-toast-cta"
        data-testid="lang-toast-switch"
      >
        {copy.cta} <span className="arrow">→</span>
      </button>
      <button
        type="button"
        onClick={onDismiss}
        className="sp-lang-toast-close"
        aria-label={copy.dismissAria}
        data-testid="lang-toast-dismiss"
      >
        ×
      </button>
    </div>
  );
};
